import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/ui/components/ui/card";
import { Button } from "@/ui/components/ui/button";
import { Badge } from "@/ui/components/ui/badge";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/components/ui/select";
import { 
  FileSpreadsheet, 
  Plus, 
  Loader2,
  FileText,
  BarChart3
} from "lucide-react";
import { useCreateReportJob } from "@/export/api/queries/reports.queries";
import { ReportProgressToast } from "./report-progress-toast";
import { BouldersReportParams, ActivityReportParams, JobType } from "@/export/types/reports.ts";
import { formatDateTime } from "@/export/utils/date-utils";
import { formatDate } from "@/shared/lib/i18n/formatters.ts";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface ReportExportFormProps {
  selectedDevices: string[];
  startDate: Date | undefined;
  endDate: Date | undefined;
  startTime: string;
  endTime: string;
  selectedLocationId?: string;
  onJobCreated?: (jobId: string) => void;
  className?: string;
}

export const ReportExportForm: React.FC<ReportExportFormProps> = ({
  selectedDevices,
  startDate,
  endDate,
  startTime,
  endTime,
  selectedLocationId,
  onJobCreated,
  className,
}) => {
  const { t } = useTranslation("export");
  const [createdJobId, setCreatedJobId] = useState<string | null>(null);
  const [selectedReportType, setSelectedReportType] = useState<JobType>('boulders_report');
  
  const createJobMutation = useCreateReportJob();

  const handleCreateReport = () => {
    if (selectedDevices.length === 0) {
      toast.error(t("toast.selectDevice"));
      return;
    }

    if (!startDate || !endDate || !startTime || !endTime) {
      toast.error(t("toast.selectTimeRange"));
      return;
    }

    const params: BouldersReportParams | ActivityReportParams = {
      device_mxids: selectedDevices,
      start_time: formatDateTime(startDate, startTime),
      end_time: formatDateTime(endDate, endTime),
      ...(selectedReportType === 'boulders_report' && selectedLocationId && {
        location_id: parseInt(selectedLocationId)
      })
    };

    createJobMutation.mutate(
      { type: selectedReportType, params },
      {
        onSuccess: (response) => {
          if (response.data) {
            setCreatedJobId(response.data.id);
            onJobCreated?.(response.data.id);
            // Progress toast will be handled by ReportProgressToast component
          }
        },
      }
    );
  };

  const canCreateNewReport = !createdJobId;
  const isCreating = createJobMutation.isPending;

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5" />
          {t("form.title")}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Report Type Selector */}
        <div className="space-y-2">
          <label className="text-sm font-medium">{t("form.reportTypeLabel")}</label>
          <Select value={selectedReportType} onValueChange={(value: JobType) => setSelectedReportType(value)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="boulders_report">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  <div>
                    <div className="font-medium">{t("reportType.boulders")}</div>
                    <div className="text-xs text-muted-foreground">{t("form.bouldersDescription")}</div>
                  </div>
                </div>
              </SelectItem>
              <SelectItem value="activity_report">
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  <div>
                    <div className="font-medium">{t("reportType.activity")}</div>
                    <div className="text-xs text-muted-foreground">{t("form.activityDescription")}</div>
                  </div>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Export Summary */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">{t("form.devicesLabel")}</span>
            <Badge variant="secondary">
              {t("form.devicesSelected", { count: selectedDevices.length })}
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">{t("form.timeRangeLabel")}</span>
            <span className="text-sm font-medium">
              {startDate && formatDate(startDate)} - {endDate && formatDate(endDate)}
            </span>
          </div>
        </div>

        {/* Export Button */}
        {canCreateNewReport && (
          <Button
            onClick={handleCreateReport}
            disabled={isCreating || selectedDevices.length === 0}
            className="w-full"
          >
            {isCreating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t("form.submitting")}
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                {t("form.submit")}
              </>
            )}
          </Button>
        )}

        {/* Progress Toast Component */}
        {createdJobId && (
          <ReportProgressToast 
            jobId={createdJobId}
            onComplete={(job) => {
              if (job.status === 'SUCCESS') {
                toast.success(t("toast.completed"));
              } else if (job.status === 'FAILED') {
                toast.error(t("toast.failed"));
              }
            }}
          />
        )}

        {/* Reset Button */}
        {createdJobId && (
          <Button
            variant="outline"
            onClick={() => setCreatedJobId(null)}
            className="w-full"
          >
            {t("form.reset")}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
